import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAuthedBlobUrl } from '../../hooks/useAuthedBlobUrl';

interface AuthedKeystoneImageProps {
  /** The Keystone-issued path, e.g. `/keystone/thumbnail/<id>`. */
  publicUrl: string;
  alt: string;
  style?: React.CSSProperties;
}

/**
 * Tiny wrapper around `useAuthedBlobUrl` for the Keystone thumbnail
 * endpoint. Required because the thumbnails are rendered inside
 * `.map()` callbacks — hooks can't go in a loop, but a child
 * component can call the hook at its own top level.
 *
 * Renders a small spinner placeholder while the bytes are in flight,
 * then swaps to the blob-backed `<img>`.
 */
export const AuthedKeystoneImage: React.FC<AuthedKeystoneImageProps> = ({
  publicUrl,
  alt,
  style,
}) => {
  const blobUrl = useAuthedBlobUrl(publicUrl);
  if (!blobUrl) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ width: '100%', height: '100%', minHeight: 60, ...style }}
      >
        <CircularProgress size={16} />
      </Box>
    );
  }
  return <img src={blobUrl} alt={alt} style={style} />;
};
